import { Dispatch, memo, SetStateAction, useEffect, useMemo } from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
  useGetAllSystemDetailCodesDropdown,
} from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import { useTranslation } from "react-i18next";
import { SYSTEM_DETAIL_CODES, USER_ROLES_IDS } from "../../constants/common";
import SelectableFilterGroup from "../inputs/SelectableFilterGroup";
import {
  useGetAllEvacuationEventsCodes,
  useGetAllEvacuationEventsParticipants,
  useGetMusterPointsDropdownByLocation,
  useMusterPointsByUser,
} from "../../hooks/emergency-evacuation/query";
import useAuthStore from "../../store/Auth";

interface LiveEvacuationDashboardFiltersCardProps {
  isVisitReqType?: boolean;
  setAllEventParticipants: Dispatch<SetStateAction<never[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
  selectedEventCode: string;
  setSelectedEventCode: Dispatch<SetStateAction<string>>;
  selectedLocationId: string;
  setSelectedLocationId: Dispatch<SetStateAction<string>>;
  selectedBuildingId: string;
  setSelectedBuildingId: Dispatch<SetStateAction<string>>;
  selectedStatuses: number[];
  setSelectedStatuses: Dispatch<SetStateAction<number[]>>;
  selectedUserTypes: number[];
  setSelectedUserTypes: Dispatch<SetStateAction<number[]>>;
  selectedMusterPoints: number[];
  setSelectedMusterPoints: Dispatch<SetStateAction<number[]>>;
}

const LiveEvacuationDashboardSchema = Yup.object({
  evacuationEventsCode: Yup.string(),
  location: Yup.string(),
  building: Yup.string(),
  floor: Yup.string(),
});

const LiveEvacuationDashboardFiltersCard = ({
  setAllEventParticipants,
  setIsLoading,
  page,
  setPages,
  search,
  setCount,
  pageSize,
  setPage,
  selectedEventCode,
  setSelectedEventCode,
  selectedLocationId,
  setSelectedLocationId,
  selectedBuildingId,
  setSelectedBuildingId,
  selectedStatuses,
  setSelectedStatuses,
  selectedUserTypes,
  setSelectedUserTypes,
  selectedMusterPoints,
  setSelectedMusterPoints,
}: LiveEvacuationDashboardFiltersCardProps) => {
  const { t } = useTranslation();

  const { user } = useAuthStore();

  const {
    control,
    formState: { errors },
    setValue,
  } = useFormWithSchema(LiveEvacuationDashboardSchema);

  const { data: allEvacuationEventsCodes } = useGetAllEvacuationEventsCodes();
  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId ? selectedLocationId.toString() : "",
  });
  const { data: allEventStatuses } = useGetAllSystemDetailCodesDropdown({
    encryptedId:
      SYSTEM_DETAIL_CODES.EMERGENCY_EVACUATION_EVENT_PARITICIPANT_STATUSES,
  });
  const { data: allPersonTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.EMERGENCY_EVACUATION_PERSON_TYPE,
  });
  const { data: allMusterPoints } = useGetMusterPointsDropdownByLocation(
    {
      n_LocationID: selectedLocationId ? Number(selectedLocationId) : 0,
      n_BuildingID: selectedBuildingId ? Number(selectedBuildingId) : 0,
    },
    user?.n_RoleID === USER_ROLES_IDS.ADMIN,
  );

  const evacuationStatuses = useMemo(() => {
    return mapToOptions(
      "s_Desc_E",
      "s_Desc_A",
      "n_DetailedCode",
      allEventStatuses,
    );
  }, [allEventStatuses]);

  const personTypes = useMemo(() => {
    return mapToOptions(
      "s_Desc_E",
      "s_Desc_A",
      "n_DetailedCode",
      allPersonTypes,
    );
  }, [allPersonTypes]);

  const {
    data: allEvacuationEventsParticipants,
    isPending: isAllEvacuationEventsParticipantsLoading,
    refetch: refetchAllEvacuationEventsParticipants,
  } = useGetAllEvacuationEventsParticipants({
    s_EvacuationCode: selectedEventCode,
    n_LocationID: Number(selectedLocationId),
    n_BuildingID: Number(selectedBuildingId),
    s_EventPersonTypeCodes: selectedUserTypes.join(","),
    s_MusterPointIDs: selectedMusterPoints.join(","),
    s_EvacuationStatus: selectedStatuses.join(","),
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      refetchAllEvacuationEventsParticipants();
    }, 5000);

    return () => clearInterval(interval);
  }, [refetchAllEvacuationEventsParticipants]);

  const musterPoints = useMemo(
    () =>
      mapToOptions(
        "s_MusterPointName_En",
        "s_MusterPointName_Ar",
        "n_MusterPointID",
        allMusterPoints,
      ),
    [allMusterPoints],
  );

  const { data: allMusterPointsByUser } = useMusterPointsByUser(
    user?.n_RoleID !== USER_ROLES_IDS.ADMIN,
  );

  const musterPointsByUser = useMemo(
    () =>
      mapToOptions(
        "s_MusterPointName_En",
        "s_MusterPointName_Ar",
        "n_MusterPointID",
        allMusterPointsByUser,
      ),
    [allMusterPointsByUser],
  );

  useEffect(() => {
    if (allEvacuationEventsParticipants) {
      setAllEventParticipants(allEvacuationEventsParticipants.data || []);
      setPages(allEvacuationEventsParticipants.pagination?.pages || 1);
      setCount(allEvacuationEventsParticipants.pagination?.count || 0);
    }
  }, [
    setCount,
    setAllEventParticipants,
    setPages,
    allEvacuationEventsParticipants,
  ]);

  useEffect(() => {
    setIsLoading?.(isAllEvacuationEventsParticipantsLoading);
  }, [isAllEvacuationEventsParticipantsLoading, setIsLoading]);

  useEffect(() => {
    setPage(1);
  }, [
    selectedBuildingId,
    selectedLocationId,
    selectedMusterPoints,
    selectedStatuses,
    selectedUserTypes,
    search,
    setPage,
  ]);

  const evacuationEventsCodes = useMemo(
    () =>
      mapToOptions(
        "s_EvacuationCode",
        "s_EvacuationCode",
        "s_EvacuationCode",
        allEvacuationEventsCodes,
      ),
    [allEvacuationEventsCodes],
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations,
      ),
    [allLocations],
  );

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings,
          )
        : [],
    [allBuildings],
  );

  return (
    <div className="bg-white max-h-max p-2 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <div className="flex flex-col gap-3 px-0.5 py-2">
        <h1 className="text-lg md:text-xl flex flex-1 -mt-2">{t("filters")}</h1>

        <div className="grid sm:grid-cols-3 gap-3 lg:grid-cols-1">
          <Controller
            name="evacuationEventsCode"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <ComboboxInput
                options={evacuationEventsCodes}
                value={
                  evacuationEventsCodes?.find((item) => item.value === value)
                    ?.labelEn
                }
                onChange={(value) => {
                  onChange(value);
                  setSelectedEventCode(value.toString());
                }}
                error={errors?.[name]?.message}
                label={t("event")}
              />
            )}
          />

          {user?.n_RoleID === USER_ROLES_IDS.ADMIN && (
            <>
              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      selectedLocationId
                        ? locations?.find(
                            (item) => item.value === Number(selectedLocationId),
                          )?.labelEn || ""
                        : ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setSelectedLocationId(value.toString());
                      setValue("building", "");
                      setSelectedBuildingId("");
                      setSelectedMusterPoints([]);
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      selectedBuildingId
                        ? buildings?.find(
                            (item) => item.value === Number(selectedBuildingId),
                          )?.labelEn || ""
                        : ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setSelectedBuildingId(value.toString());
                    }}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />
            </>
          )}
        </div>

        <SelectableFilterGroup
          label={t("status")}
          items={evacuationStatuses}
          selectedItems={selectedStatuses}
          onSelectionChange={setSelectedStatuses}
        />

        <SelectableFilterGroup
          label={t("type")}
          items={personTypes}
          selectedItems={selectedUserTypes}
          onSelectionChange={setSelectedUserTypes}
        />

        {(user?.n_RoleID !== USER_ROLES_IDS.ADMIN ||
          (selectedLocationId && musterPoints?.length > 0)) && (
          <SelectableFilterGroup
            label={t("musterPoints")}
            items={
              user?.n_RoleID === USER_ROLES_IDS.ADMIN
                ? musterPoints
                : musterPointsByUser
            }
            selectedItems={selectedMusterPoints}
            onSelectionChange={setSelectedMusterPoints}
          />
        )}
      </div>
    </div>
  );
};

export default memo(LiveEvacuationDashboardFiltersCard);
